import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { firstValueFrom, fromEvent, merge, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StorageService } from '../../utils/storageService';

interface PortalSession {
    session_id: string;
    access_expires_at: string;
    refresh_expires_at: string;
    access_received_at?: number;
    pending_request_id?: string;
}

interface SessionResponse extends PortalSession {
    token: string;
    empresa?: number;
    accessControl?: unknown[];
    accessControlRoles?: string[];
    accessControlCompanys?: number[];
}

const SESSION_KEY = 'portal_session';
const REVISION_KEY = 'portal_session_revision';

@Injectable({ providedIn: 'root' })
export class PortalSessionService {
    private readonly http: HttpClient;
    private running: { sessionId: string | undefined; promise: Promise<string> } | null = null;

    constructor(backend: HttpBackend, private storage: StorageService, private router: Router, destroyRef: DestroyRef) {
        // Login/refresh não passam novamente pelo interceptor de autenticação.
        this.http = new HttpClient(backend);
        merge(fromEvent(window, 'focus'), fromEvent(document, 'visibilitychange'))
            .pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
                if (document.visibilityState === 'visible' && this.session()) void this.token().catch(() => {});
            });
    }

    isApi(url: string): boolean {
        const target = new URL(url, window.location.origin);
        const base = new URL(environment.BASE_URL, window.location.origin);
        return target.origin === base.origin && target.pathname.startsWith(base.pathname);
    }

    session(): PortalSession | null {
        try {
            const value = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
            return typeof value?.session_id === 'string' ? value : null;
        } catch { return null; }
    }

    private locked<T>(work: () => Promise<T>): Promise<T> {
        if (!navigator.locks) return Promise.reject(new Error('Atualize o navegador para usar a sessão renovável do Portal.'));
        return navigator.locks.request('rhapp-portal-session', work);
    }

    private save(value: SessionResponse): void {
        // O refresh é HttpOnly e nunca entra no armazenamento acessível ao JavaScript.
        const previous = this.storage.getUserLogado().auth || {};
        this.storage.login({ ...previous, ...value });
        const { session_id, access_expires_at, refresh_expires_at } = value;
        localStorage.setItem(SESSION_KEY, JSON.stringify({ session_id, access_expires_at, refresh_expires_at, access_received_at: Date.now() }));
    }

    private clear(): void {
        localStorage.removeItem(SESSION_KEY);
        this.storage.logoffUser();
    }

    async login(cpf: string, senha: string): Promise<SessionResponse> {
        const revision = crypto.randomUUID();
        localStorage.setItem(REVISION_KEY, revision);
        this.clear();
        return this.locked(async () => {
            if (localStorage.getItem(REVISION_KEY) !== revision) throw new Error('Login cancelado.');
            this.clear();
            const value = await firstValueFrom(this.http.post<SessionResponse>(environment.BASE_URL + 'identity/login/v2',
                { cpf, senha, session_version: 1, client_origin: 'portal' }, { withCredentials: true }).pipe(timeout(30000)));
            if (!value.session_id || !value.token) throw new Error('A API ainda não disponibilizou a sessão renovável do Portal.');
            if (localStorage.getItem(REVISION_KEY) !== revision) {
                await firstValueFrom(this.http.post(environment.BASE_URL + 'identity/auth/logout',
                    { session_id: value.session_id }, { withCredentials: true }).pipe(timeout(15000)));
                throw new Error('Login cancelado.');
            }
            this.save(value);
            return value;
        });
    }

    async token(): Promise<string | null> {
        const current = this.session();
        if (current && this.expiresSoon(current)) return this.refresh();
        return this.storage.getToken();
    }

    private expiresSoon(current: PortalSession): boolean {
        const expiry = Date.parse(current.access_expires_at);
        const lifetime = current.access_received_at ? Math.max(0, expiry - current.access_received_at) : 0;
        return !Number.isFinite(expiry) || expiry <= Date.now() + Math.min(120000, lifetime / 10);
    }

    refresh(rejectedToken?: string): Promise<string> {
        const expected = this.session()?.session_id;
        if (this.running && this.running.sessionId === expected) return this.running.promise;
        const work = this.locked(async () => {
            const current = this.session();
            if (!expected || current?.session_id !== expected) throw new Error('Sessão alterada.');
            const token = this.storage.getToken();
            // Outra aba pode ter renovado enquanto esta aguardava a trava.
            if (token && (rejectedToken ? token !== rejectedToken : !this.expiresSoon(current))) return token;
            const requestId = current.pending_request_id || crypto.randomUUID();
            localStorage.setItem(SESSION_KEY, JSON.stringify({ ...current, pending_request_id: requestId }));
            try {
                const value = await firstValueFrom(this.http.post<SessionResponse>(environment.BASE_URL + 'identity/auth/refresh',
                    { session_id: expected, request_id: requestId }, { withCredentials: true }).pipe(timeout(15000)));
                if (this.session()?.session_id !== expected) throw new Error('Sessão alterada.');
                if (value.session_id !== expected || !value.token) throw new Error('Resposta de renovação inválida.');
                this.save(value);
                return value.token;
            } catch (error) {
                if (error instanceof HttpErrorResponse && (error.status === 401 || error.error?.code === 'PORTAL_SESSION_CHANGED')) this.end(expected);
                // Rede/5xx preservam a sessão e a mesma tentativa para recuperação.
                throw error;
            }
        });
        this.running = { sessionId: expected, promise: work };
        void work.finally(() => { if (this.running?.promise === work) this.running = null; }).catch(() => {});
        return work;
    }

    end(expected?: string): void {
        if (expected && this.session()?.session_id !== expected) return;
        this.clear();
        void this.router.navigate(['/login']);
    }

    async logout(): Promise<void> {
        localStorage.setItem(REVISION_KEY, crypto.randomUUID());
        const current = this.session();
        this.clear();
        try {
            await this.locked(async () => {
                // Um login mais recente não deve ser encerrado por logout antigo.
                if (current && !this.session()) await firstValueFrom(this.http.post(environment.BASE_URL + 'identity/auth/logout',
                    { session_id: current.session_id }, { withCredentials: true }).pipe(timeout(15000)));
            });
        } finally {
            if (!this.session()) void this.router.navigate(['/login']);
        }
    }
}
